import { z } from 'zod';
/**
 * local-ledger (local.cars-finders.com) — the owners' own car-trading book:
 * cars bought on Swiss insurance auctions → expenses → sale, with cost,
 * profit and margin in USD. A SEPARATE domain from the platform auction: its
 * collections are all `local_*` and it never reads `bids` / `won_cars` /
 * `deals` / `alpicar_cars` (CI gate: apps/api/scripts/check-local-ledger-isolation.mjs).
 *
 * Money crosses the API as a 2-decimal STRING (`"8107.00"`) plus a currency
 * code, never as a `number`; Mongo stores `Decimal128`; arithmetic is
 * `decimal.js` (see ./local-ledger-math). Dates are `YYYY-MM-DD` strings here
 * and `Date` in Mongo.
 *
 * `fxRateToUsd` = USD per ONE unit of the record's currency:
 * 1 950 CHF × 1.27 = 2 476.50 USD.
 */
export const LocalCurrencySchema = z.enum(['USD', 'CHF', 'UAH', 'EUR']);
export const LOCAL_CURRENCIES = LocalCurrencySchema.options;
/** Signed amount with exactly two decimals: `"8107.00"`, `"-12.50"`. */
export const MONEY_STRING_PATTERN = /^-?\d+\.\d{2}$/;
export const MoneyStringSchema = z.string().regex(MONEY_STRING_PATTERN, {
    message: 'Expected an amount with exactly two decimals, e.g. "8107.00"',
});
/** A money amount that must be above zero (prices, expenses, budget movements). */
export const PositiveMoneyStringSchema = MoneyStringSchema.refine((v) => !v.startsWith('-') && /[1-9]/.test(v), {
    message: 'Amount must be greater than zero',
});
/** A money amount that may be zero but never negative (a purchase price of 0 is legal). */
export const NonNegativeMoneyStringSchema = MoneyStringSchema.refine((v) => !v.startsWith('-'), {
    message: 'Amount must not be negative',
});
/** USD per one unit of currency, up to 6 decimals, strictly positive: `"1.27"`, `"0.0242"`. */
export const FX_RATE_STRING_PATTERN = /^\d+(\.\d{1,6})?$/;
export const FxRateStringSchema = z
    .string()
    .regex(FX_RATE_STRING_PATTERN, { message: 'Expected a positive rate with up to 6 decimals' })
    .refine((v) => /[1-9]/.test(v), { message: 'Rate must be greater than zero' });
/** A ratio with exactly four decimals (`margin`): `"0.0572"` = 5.72 %. */
export const RATIO_STRING_PATTERN = /^-?\d+\.\d{4}$/;
export const RatioStringSchema = z.string().regex(RATIO_STRING_PATTERN);
/** Calendar day `YYYY-MM-DD`. */
export const LocalIsoDateSchema = z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/, {
    message: 'Expected a date as YYYY-MM-DD',
});
const ObjectIdStringSchema = z
    .string()
    .regex(/^[a-f0-9]{24}$/i, { message: 'Expected an ObjectId' });
// ── Roles ────────────────────────────────────────────────────────────────
/** `local_owner` — everything; `local_manager` — vehicles/expenses/sale; `local_viewer` — read-only. */
export const LocalRoleSchema = z.enum(['local_owner', 'local_manager', 'local_viewer']);
export const LOCAL_ROLES = LocalRoleSchema.options;
// ── Enums ────────────────────────────────────────────────────────────────
/** Vehicle lifecycle, in order: purchased → … → sold. */
export const LocalVehicleStageSchema = z.enum([
    'purchased',
    'in_transit',
    'customs',
    'in_ukraine',
    'repair',
    'for_sale',
    'sold',
]);
export const LOCAL_VEHICLE_STAGES = LocalVehicleStageSchema.options;
/**
 * Where a vehicle card came from: `local_bid` — a won `local_bids` row (the
 * retired path: only the historic cards, `bidId` set — nothing writes it any
 * more); `platform_bid` — a bid won on the platform auction (`sourceBidId`
 * names it); `manual` — typed in on «Додати авто» or imported from the sheet.
 */
export const LocalVehicleSourceSchema = z.enum(['local_bid', 'platform_bid', 'manual']);
export const LocalFxRateSourceSchema = z.enum(['manual', 'nbu']);
export const LocalBudgetMovementTypeSchema = z.enum(['deposit', 'withdrawal', 'opening']);
/**
 * `bid` is kept for HISTORIC entries only: the local bids (screen and API)
 * were retired, but the cards won from a local bid still show their bid's
 * create/update entries in «Історія».
 */
export const LocalAuditEntitySchema = z.enum([
    'auction',
    'bid',
    'vehicle',
    'expense_category',
    'expense',
    'fx_rate',
    'budget_movement',
    'file',
]);
export const LocalAuditActionSchema = z.enum(['create', 'update', 'delete']);
export const LocalFileKindSchema = z.enum(['photo', 'receipt']);
/** Upload cap for vehicle photos and expense receipts. */
export const LOCAL_FILE_MAX_BYTES = 10 * 1024 * 1024;
/**
 * The upload whitelist. Exact, lower-case MIME types: the type is pinned into
 * the V4 signature of the upload URL, so the browser must PUT with this very
 * `Content-Type` (a `File.type` is already lower-case).
 */
export const LOCAL_FILE_CONTENT_TYPES = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/heic',
    'application/pdf',
];
export const LocalFileContentTypeSchema = z.enum(LOCAL_FILE_CONTENT_TYPES, {
    errorMap: () => ({ message: 'Only JPEG, PNG, WebP, HEIC images and PDF are accepted' }),
});
// ── Auction ──────────────────────────────────────────────────────────────
export const LocalAuctionSchema = z.object({
    id: z.string(),
    name: z.string(),
    note: z.string().nullable(),
    active: z.boolean(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export const LocalAuctionCreateSchema = z
    .object({
    name: z.string().trim().min(1).max(80),
    note: z.string().trim().max(500).nullable().optional(),
})
    .strict();
export const LocalAuctionUpdateSchema = LocalAuctionCreateSchema.partial()
    .extend({ active: z.boolean().optional() })
    .strict();
// ── Lot URL ──────────────────────────────────────────────────────────────
/**
 * True only for an absolute `http:` / `https:` URL. A lot link is rendered as
 * `<a href>`, so `javascript:` / `data:` would run in the app's origin when
 * clicked — the schema refuses them on write and the UI re-checks on render.
 */
export function isHttpUrl(value) {
    try {
        const { protocol } = new URL(value);
        return protocol === 'http:' || protocol === 'https:';
    }
    catch {
        return false;
    }
}
const LotUrlSchema = z
    .string()
    .trim()
    .url()
    .max(2048)
    .refine(isHttpUrl, { message: 'Expected an http(s) URL' });
// ── Vehicle ──────────────────────────────────────────────────────────────
/**
 * Materialized by the service on every write that touches a number below
 * (never recomputed from today's FX table — SC-7). Every money field is null
 * when a rate it depends on is missing; `status: 'incomplete'` flags that.
 * `saleUsd` / `profitUsd` / `margin` are null unless `stage = sold`; `margin`
 * is also null when `costUsd = 0`.
 */
export const LocalVehicleFinancialsSchema = z.object({
    status: z.enum(['ok', 'incomplete']),
    purchaseUsd: MoneyStringSchema.nullable(),
    expensesUsd: MoneyStringSchema.nullable(),
    costUsd: MoneyStringSchema.nullable(),
    saleUsd: MoneyStringSchema.nullable(),
    profitUsd: MoneyStringSchema.nullable(),
    /** `profitUsd / costUsd`, four decimals. */
    margin: RatioStringSchema.nullable(),
    daysInStock: z.number().int().nullable(),
    /** Σ amount × (fxRateToUsd − officialFxRateToUsd) over lines where the official rate is known. */
    fxDeviationUsd: MoneyStringSchema.nullable(),
});
export const LocalVehicleSchema = z.object({
    id: z.string(),
    /** The (retired) `local_bids` row a historic card was won from; null for every other card. */
    bidId: z.string().nullable(),
    /** The platform bid (admin `/bids/:id`) the card was won from; null unless `source = platform_bid`. */
    sourceBidId: z.string().nullable(),
    source: LocalVehicleSourceSchema,
    auctionId: z.string(),
    make: z.string(),
    model: z.string(),
    year: z.number().int().nullable(),
    vin: z.string().nullable(),
    mileage: z.number().int().nullable(),
    lotUrl: z.string().nullable(),
    stage: LocalVehicleStageSchema,
    purchasePrice: MoneyStringSchema,
    purchaseCurrency: LocalCurrencySchema,
    /** Nullable only for imported rows that never had a rate. */
    purchaseFxRateToUsd: FxRateStringSchema.nullable(),
    purchaseOfficialFxRateToUsd: FxRateStringSchema.nullable(),
    purchaseDate: LocalIsoDateSchema,
    salePrice: MoneyStringSchema.nullable(),
    saleCurrency: LocalCurrencySchema.nullable(),
    saleFxRateToUsd: FxRateStringSchema.nullable(),
    saleOfficialFxRateToUsd: FxRateStringSchema.nullable(),
    saleDate: LocalIsoDateSchema.nullable(),
    buyerName: z.string().nullable(),
    note: z.string().nullable(),
    financials: LocalVehicleFinancialsSchema,
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
/**
 * `POST /api/local/vehicles` — «Додати авто»: a card typed in by hand
 * (`source = manual`, no bid). The rate is OPTIONAL here: without one (and not
 * USD, which is always 1) the card is saved with `financials.status =
 * 'incomplete'` until «Курс покупки» completes it.
 */
export const LocalVehicleCreateSchema = z
    .object({
    auctionId: ObjectIdStringSchema,
    purchaseDate: LocalIsoDateSchema,
    make: z.string().trim().min(1).max(60),
    model: z.string().trim().min(1).max(80),
    year: z.number().int().min(1950).max(2100).nullable().optional(),
    purchasePrice: NonNegativeMoneyStringSchema,
    purchaseCurrency: LocalCurrencySchema,
    purchaseFxRateToUsd: FxRateStringSchema.nullable().optional(),
    vin: z.string().trim().max(32).nullable().optional(),
    mileage: z.number().int().min(0).nullable().optional(),
    lotUrl: LotUrlSchema.nullable().optional(),
    note: z.string().trim().max(1000).nullable().optional(),
})
    .strict();
export const LocalVehicleUpdateSchema = z
    .object({
    make: z.string().trim().min(1).max(60),
    model: z.string().trim().min(1).max(80),
    year: z.number().int().min(1950).max(2100).nullable(),
    vin: z.string().trim().max(32).nullable(),
    mileage: z.number().int().min(0).nullable(),
    lotUrl: LotUrlSchema.nullable(),
    stage: LocalVehicleStageSchema,
    purchasePrice: NonNegativeMoneyStringSchema,
    purchaseCurrency: LocalCurrencySchema,
    purchaseFxRateToUsd: FxRateStringSchema,
    purchaseDate: LocalIsoDateSchema,
    note: z.string().trim().max(1000).nullable(),
})
    .partial()
    .strict();
/** Recording a sale moves the vehicle to `sold`. */
export const LocalVehicleSaleSchema = z
    .object({
    salePrice: PositiveMoneyStringSchema,
    saleCurrency: LocalCurrencySchema,
    saleFxRateToUsd: FxRateStringSchema,
    saleDate: LocalIsoDateSchema,
    buyerName: z.string().trim().max(120).nullable().optional(),
})
    .strict();
// ── Expense category ─────────────────────────────────────────────────────
/** Seeded by the ledger API (phase 3); codes are stable, names are editable. */
export const LOCAL_DEFAULT_EXPENSE_CATEGORIES = [
    { code: 'repair', name: 'Ремонт' },
    { code: 'intermediary', name: 'Посередник (Олег)' },
    { code: 'delivery', name: 'Доставка' },
    { code: 'misc', name: 'Інше' },
    { code: 'export_plates', name: 'Експортні номери' },
    { code: 'customs', name: 'Розмитнення' },
    { code: 'terminal', name: 'Термінал' },
    { code: 'certificate', name: 'Сертифікат' },
];
const ExpenseCategoryCodeSchema = z
    .string()
    .trim()
    .regex(/^[a-z][a-z0-9_]{1,39}$/, { message: 'Use lowercase latin, digits and _' });
export const LocalExpenseCategorySchema = z.object({
    id: z.string(),
    code: z.string(),
    name: z.string(),
    active: z.boolean(),
    sortOrder: z.number().int(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export const LocalExpenseCategoryCreateSchema = z
    .object({
    code: ExpenseCategoryCodeSchema,
    name: z.string().trim().min(1).max(80),
    sortOrder: z.number().int().min(0).optional(),
})
    .strict();
/** `code` is immutable once created — reports and the import key on it. */
export const LocalExpenseCategoryUpdateSchema = z
    .object({
    name: z.string().trim().min(1).max(80),
    sortOrder: z.number().int().min(0),
    active: z.boolean(),
})
    .partial()
    .strict();
// ── Expense ──────────────────────────────────────────────────────────────
export const LocalExpenseSchema = z.object({
    id: z.string(),
    vehicleId: z.string(),
    categoryId: z.string(),
    date: LocalIsoDateSchema,
    amount: MoneyStringSchema,
    currency: LocalCurrencySchema,
    /** Nullable only for imported rows; an HTTP write must carry it (SC-5). */
    fxRateToUsd: FxRateStringSchema.nullable(),
    /** From `local_fx_rates` on `date`, filled by the service; null when unknown. */
    officialFxRateToUsd: FxRateStringSchema.nullable(),
    note: z.string().nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export const LocalExpenseCreateSchema = z
    .object({
    vehicleId: ObjectIdStringSchema,
    categoryId: ObjectIdStringSchema,
    date: LocalIsoDateSchema,
    amount: PositiveMoneyStringSchema,
    currency: LocalCurrencySchema,
    fxRateToUsd: FxRateStringSchema,
    note: z.string().trim().max(1000).nullable().optional(),
})
    .strict();
/** `vehicleId` is immutable — move an expense by deleting and re-creating it. */
export const LocalExpenseUpdateSchema = LocalExpenseCreateSchema.omit({ vehicleId: true })
    .extend({ note: z.string().trim().max(1000).nullable() })
    .partial()
    .strict();
// ── FX rate ──────────────────────────────────────────────────────────────
/** One reference rate per (date, currency). Changing it never rewrites saved financials (SC-7). */
export const LocalFxRateSchema = z.object({
    id: z.string(),
    date: LocalIsoDateSchema,
    currency: LocalCurrencySchema,
    rateToUsd: FxRateStringSchema,
    source: LocalFxRateSourceSchema,
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
/** USD is the base — a USD→USD rate is always 1 and is never stored. */
const NonUsdCurrencySchema = LocalCurrencySchema.exclude(['USD']);
export const LocalFxRateCreateSchema = z
    .object({
    date: LocalIsoDateSchema,
    currency: NonUsdCurrencySchema,
    rateToUsd: FxRateStringSchema,
})
    .strict();
export const LocalFxRateUpdateSchema = z.object({ rateToUsd: FxRateStringSchema }).strict();
// ── Budget movement ──────────────────────────────────────────────────────
/** `amount` is a positive magnitude; `type` gives the sign (withdrawal subtracts). */
export const LocalBudgetMovementSchema = z.object({
    id: z.string(),
    type: LocalBudgetMovementTypeSchema,
    date: LocalIsoDateSchema,
    amount: MoneyStringSchema,
    currency: LocalCurrencySchema,
    fxRateToUsd: FxRateStringSchema,
    amountUsd: MoneyStringSchema,
    note: z.string().nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export const LocalBudgetMovementCreateSchema = z
    .object({
    type: LocalBudgetMovementTypeSchema,
    date: LocalIsoDateSchema,
    amount: PositiveMoneyStringSchema,
    currency: LocalCurrencySchema,
    fxRateToUsd: FxRateStringSchema,
    note: z.string().trim().max(1000).nullable().optional(),
})
    .strict();
export const LocalBudgetMovementUpdateSchema = LocalBudgetMovementCreateSchema.extend({
    note: z.string().trim().max(1000).nullable(),
})
    .partial()
    .strict();
/** `GET /api/local/budget/balance` — see `computeBudgetBalance`. */
export const LocalBudgetBalanceSchema = z.object({
    balanceUsd: MoneyStringSchema,
    inflowsUsd: MoneyStringSchema,
    withdrawalsUsd: MoneyStringSchema,
    purchasesUsd: MoneyStringSchema,
    expensesUsd: MoneyStringSchema,
    salesUsd: MoneyStringSchema,
    /** Vehicles whose purchase / expenses / sale USD is unknown and therefore left out. */
    incompleteVehicles: z.number().int().min(0),
});
// ── Audit ────────────────────────────────────────────────────────────────
export const LocalAuditChangeSchema = z.object({
    field: z.string(),
    from: z.unknown(),
    to: z.unknown(),
});
/** Append-only «who, when, old → new» (`local_audit_log`). Written by the service, never by a client. */
export const LocalAuditEntrySchema = z.object({
    id: z.string(),
    entity: LocalAuditEntitySchema,
    entityId: z.string(),
    action: LocalAuditActionSchema,
    actorId: z.string(),
    actorEmail: z.string().nullable(),
    at: z.coerce.date(),
    changes: z.array(LocalAuditChangeSchema),
});
/** What the service appends; there is no update DTO — the log is append-only. */
export const LocalAuditEntryCreateSchema = LocalAuditEntrySchema.omit({
    id: true,
    at: true,
}).strict();
// ── File ─────────────────────────────────────────────────────────────────
/** A photo of a vehicle or a receipt of an expense, in a private GCS bucket. */
export const LocalFileSchema = z.object({
    id: z.string(),
    vehicleId: z.string(),
    expenseId: z.string().nullable(),
    kind: LocalFileKindSchema,
    objectKey: z.string(),
    fileName: z.string(),
    contentType: z.string(),
    sizeBytes: z.number().int().positive(),
    uploadedBy: z.string(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
/** Request for a signed upload URL. A receipt must name its expense. */
export const LocalFileCreateSchema = z
    .object({
    vehicleId: ObjectIdStringSchema,
    expenseId: ObjectIdStringSchema.nullable().optional(),
    kind: LocalFileKindSchema,
    fileName: z.string().trim().min(1).max(200),
    contentType: LocalFileContentTypeSchema,
    sizeBytes: z.number().int().positive().max(LOCAL_FILE_MAX_BYTES),
})
    .strict()
    .superRefine((body, ctx) => {
    if (body.kind === 'receipt' && !body.expenseId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['expenseId'],
            message: 'A receipt belongs to an expense',
        });
    }
});
/**
 * `POST local/files/sign-upload` answer: PUT the bytes to `uploadUrl` with
 * exactly `headers`, then `POST local/files/:fileId/confirm` with the same body
 * the ticket was signed for. Nothing is written to `local_files` until then.
 */
export const LocalFileUploadTicketSchema = z.object({
    fileId: z.string(),
    objectKey: z.string(),
    uploadUrl: z.string().url(),
    method: z.literal('PUT'),
    headers: z.object({ 'Content-Type': LocalFileContentTypeSchema }),
    expiresAt: z.coerce.date(),
});
/** `GET local/files/:id/url` — a short-lived signed GET; never stored, never logged. */
export const LocalFileReadUrlSchema = z.object({
    url: z.string().url(),
    expiresAt: z.coerce.date(),
});
/** Files are immutable objects — only the display name may change. */
export const LocalFileUpdateSchema = z
    .object({ fileName: z.string().trim().min(1).max(200) })
    .strict();
// ── Ledger API: lists, pages, write results ─────────────────────────────
const PageFieldsSchema = {
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(200).default(50),
};
const DateRangeFieldsSchema = {
    from: LocalIsoDateSchema.optional(),
    to: LocalIsoDateSchema.optional(),
};
const dateRangeOrdered = (f) => !f.from || !f.to || f.from <= f.to;
const DATE_RANGE_MESSAGE = { path: ['to'], message: '`to` must not be before `from`' };
/** `GET /api/local/vehicles` — `dateBasis` picks which date `from`/`to` filter on. */
export const LOCAL_VEHICLE_SORTS = [
    'purchaseDate',
    '-purchaseDate',
    'saleDate',
    '-saleDate',
    'make',
    '-make',
    'createdAt',
    '-createdAt',
    'costUsd',
    '-costUsd',
    'profitUsd',
    '-profitUsd',
    'margin',
    '-margin',
];
/** The vehicle filters shared by the list, the reports and the export. */
const VehicleFilterFieldsSchema = {
    ...DateRangeFieldsSchema,
    dateBasis: z.enum(['purchase', 'sale']).default('purchase'),
    auctionId: ObjectIdStringSchema.optional(),
    stage: LocalVehicleStageSchema.optional(),
    make: z.string().trim().min(1).max(60).optional(),
};
export const LocalVehicleListQuerySchema = z
    .object({
    ...VehicleFilterFieldsSchema,
    ...PageFieldsSchema,
    sort: z.enum(LOCAL_VEHICLE_SORTS).default('-purchaseDate'),
})
    .strict()
    .refine(dateRangeOrdered, DATE_RANGE_MESSAGE);
/** `GET /api/local/export/vehicles.{xlsx,csv}` — the list's filters and sort, without paging (every row). */
export const LocalVehicleExportQuerySchema = z
    .object({
    ...VehicleFilterFieldsSchema,
    sort: z.enum(LOCAL_VEHICLE_SORTS).default('-purchaseDate'),
})
    .strict()
    .refine(dateRangeOrdered, DATE_RANGE_MESSAGE);
/** Every paged list: `{ items, total, page, limit }`. */
export const localPageSchema = (item) => z.object({
    items: z.array(item),
    total: z.number().int().min(0),
    page: z.number().int().min(1),
    limit: z.number().int().min(1),
});
/**
 * Totals of the WHOLE filtered vehicle set (not just the page). Each sum skips
 * the vehicles whose figure is unknown; `incomplete` counts them so the UI can
 * say the total is partial. `saleUsd` / `profitUsd` sum sold vehicles only.
 */
export const LocalVehicleTotalsSchema = z.object({
    count: z.number().int().min(0),
    purchaseUsd: MoneyStringSchema,
    expensesUsd: MoneyStringSchema,
    costUsd: MoneyStringSchema,
    saleUsd: MoneyStringSchema,
    profitUsd: MoneyStringSchema,
    incomplete: z.number().int().min(0),
});
export const LocalVehiclePageSchema = localPageSchema(LocalVehicleSchema).extend({
    totals: LocalVehicleTotalsSchema,
});
/** `GET /api/local/vehicles/:id` — the card: the vehicle, its expenses and its history (newest first). */
export const LocalVehicleDetailSchema = LocalVehicleSchema.extend({
    expenses: z.array(LocalExpenseSchema),
    audit: z.array(LocalAuditEntrySchema),
});
/** Expense writes answer with the vehicle's freshly materialized `financials`. */
export const LocalExpenseWriteResultSchema = z.object({
    expense: LocalExpenseSchema,
    financials: LocalVehicleFinancialsSchema,
});
/** `GET /api/local/fx-rates?from&to&currency`. */
export const LocalFxRateListQuerySchema = z
    .object({
    ...DateRangeFieldsSchema,
    currency: LocalCurrencySchema.optional(),
})
    .strict()
    .refine(dateRangeOrdered, DATE_RANGE_MESSAGE);
/** `GET /api/local/fx-rates/lookup?date&currency`. */
export const LocalFxRateLookupQuerySchema = z
    .object({ date: LocalIsoDateSchema, currency: LocalCurrencySchema })
    .strict();
/**
 * The reference rate nearest on or before `date` (form prefill). USD answers
 * `"1"` with `rateDate = date` and `source = null`; no row → all three null.
 */
export const LocalFxRateLookupSchema = z.object({
    currency: LocalCurrencySchema,
    date: LocalIsoDateSchema,
    rateDate: LocalIsoDateSchema.nullable(),
    rateToUsd: FxRateStringSchema.nullable(),
    source: LocalFxRateSourceSchema.nullable(),
});
/** `GET /api/local/audit?entity&entityId` — newest first. */
export const LocalAuditQuerySchema = z
    .object({ entity: LocalAuditEntitySchema, entityId: ObjectIdStringSchema })
    .strict();
// ── Budget ───────────────────────────────────────────────────────────────
/** `GET /api/local/budget/movements?from&to&type` — `from`/`to` filter `date` (inclusive); newest first. */
export const LocalBudgetMovementListQuerySchema = z
    .object({
    ...DateRangeFieldsSchema,
    type: LocalBudgetMovementTypeSchema.optional(),
})
    .strict()
    .refine(dateRangeOrdered, DATE_RANGE_MESSAGE);
// ── Reports ──────────────────────────────────────────────────────────────
/**
 * Query of `GET /api/local/reports/*`. `dateBasis` picks which date
 * `from`/`to` filter on (`purchaseDate` or `saleDate`); the other filters
 * match `GET /api/local/vehicles`. All figures are USD — the display currency
 * is the client's business.
 */
export const LocalReportFiltersSchema = z
    .object(VehicleFilterFieldsSchema)
    .strict()
    .refine(dateRangeOrdered, DATE_RANGE_MESSAGE);
export const LOCAL_ATTENTION_STALE_DAYS = 60;
/** `GET /api/local/reports/attention` — the report filters plus how old open stock must be to count as stale. */
export const LocalReportAttentionQuerySchema = z
    .object({
    ...VehicleFilterFieldsSchema,
    staleDays: z.coerce.number().int().min(1).max(3650).default(LOCAL_ATTENTION_STALE_DAYS),
})
    .strict()
    .refine(dateRangeOrdered, DATE_RANGE_MESSAGE);
/**
 * `GET /api/local/reports/summary` — dashboard KPIs.
 *   profitUsd / soldCount / avgProfitUsd / avgMargin — vehicles SOLD in the
 *     period (always by `saleDate`, whatever `dateBasis` says);
 *   inStockCount / inStockCostUsd — open stock now (`stage ≠ sold`; the
 *     period does not apply to a snapshot);
 *   budgetBalanceUsd — the whole budget (`computeBudgetBalance`), unfiltered.
 * Averages and ratios are null when there is nothing to divide by.
 */
export const LocalReportSummarySchema = z.object({
    profitUsd: MoneyStringSchema,
    soldCount: z.number().int().min(0),
    avgProfitUsd: MoneyStringSchema.nullable(),
    avgMargin: RatioStringSchema.nullable(),
    inStockCount: z.number().int().min(0),
    inStockCostUsd: MoneyStringSchema,
    budgetBalanceUsd: MoneyStringSchema,
});
/** `GET /api/local/reports/by-month` — sold vehicles grouped by `YYYY-MM` of `saleDate`, oldest first. */
export const LocalReportMonthRowSchema = z.object({
    month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
    profitUsd: MoneyStringSchema,
    soldCount: z.number().int().min(0),
});
/**
 * One group of `by-auction` / `by-make`: `count` vehicles costing `costUsd`;
 * `soldCount` of them sold for `profitUsd`; `margin` = profitUsd / the cost of
 * those sold ones (null when none is sold with a known profit).
 */
const ReportGroupFieldsSchema = {
    count: z.number().int().min(0),
    costUsd: MoneyStringSchema,
    soldCount: z.number().int().min(0),
    profitUsd: MoneyStringSchema,
    margin: RatioStringSchema.nullable(),
};
export const LocalReportAuctionRowSchema = z.object({
    auctionId: z.string(),
    /** Null when the auction was deleted from the dictionary. */
    auctionName: z.string().nullable(),
    ...ReportGroupFieldsSchema,
});
export const LocalReportMakeRowSchema = z.object({ make: z.string(), ...ReportGroupFieldsSchema });
/**
 * `GET /api/local/reports/by-expense-category` — Σ amount × fxRateToUsd of the
 * filtered vehicles' expenses, per category. `incomplete` counts the lines left
 * out because their rate is unknown.
 */
export const LocalReportExpenseCategoryRowSchema = z.object({
    categoryId: z.string(),
    code: z.string().nullable(),
    name: z.string().nullable(),
    amountUsd: MoneyStringSchema,
    count: z.number().int().min(0),
    incomplete: z.number().int().min(0),
});
export const LocalReportAttentionVehicleSchema = LocalVehicleSchema.pick({
    id: true,
    auctionId: true,
    make: true,
    model: true,
    year: true,
    stage: true,
    purchaseDate: true,
    saleDate: true,
}).extend({ daysInStock: z.number().int() });
/**
 * `GET /api/local/reports/attention` — what needs a look:
 *   noFx — `financials.status = incomplete` (a rate is missing);
 *   soldWithoutExpenses — sold with no expense booked;
 *   stale — not sold and bought more than `staleDays` ago (oldest first).
 */
export const LocalReportAttentionSchema = z.object({
    staleDays: z.number().int().min(1),
    noFx: z.array(LocalReportAttentionVehicleSchema),
    soldWithoutExpenses: z.array(LocalReportAttentionVehicleSchema),
    stale: z.array(LocalReportAttentionVehicleSchema),
});
// ── FX sync (NBU) ────────────────────────────────────────────────────────
/** The NBU codes the sync reads; CHF/EUR/UAH are stored, USD is only the divisor. */
export const LOCAL_NBU_SYNC_CODES = ['USD', 'CHF', 'EUR'];
/**
 * `POST /api/local/fx-rates/sync` (owner) and the daily cron — one pull of
 * the NBU rates for `date` (the Kyiv calendar day):
 *   ok      — `upserted` written with `source = nbu`; `keptManual` left alone
 *             because a `manual` rate exists for that day (never overwritten);
 *   skipped — another replica holds the sync lease, nothing was done;
 *   failed  — the NBU (or the write) failed; `error` says why, nothing thrown.
 */
export const LocalFxSyncStatusSchema = z.enum(['ok', 'skipped', 'failed']);
export const LocalFxSyncResultSchema = z.object({
    date: LocalIsoDateSchema,
    status: LocalFxSyncStatusSchema,
    upserted: z.array(LocalCurrencySchema),
    keptManual: z.array(LocalCurrencySchema),
    error: z.string().nullable(),
});
// ── Users (read-only) ────────────────────────────────────────────────────
/**
 * `GET /api/local/users` (owner) — the accounts holding a `local_*` role.
 * Read-only: accounts are made and changed with the `local:users` script
 * (apps/api/scripts/local-ledger-users.ts); no password or hash ever leaves.
 */
export const LocalUserSchema = z.object({
    id: z.string(),
    email: z.string(),
    roles: z.array(LocalRoleSchema),
    /** Null for an account whose row carries no creation date. */
    createdAt: z.coerce.date().nullable(),
});
// ── The admin bridge (local-ledger-admin-bridge) ─────────────────────────
/**
 * `POST /api/admin/bids/:bidId/local-vehicle` (platform admin) — a won ADMIN
 * bid becomes a `source = platform_bid` card with no purchase rate (it is
 * listed under «немає курсу» until the owner sets one). 201 with the new card
 * (`existing: false`); 200 with the card the bid already became
 * (`existing: true`) — the call is idempotent per bid.
 */
export const LocalLedgerBridgeResultSchema = z.object({
    vehicle: LocalVehicleSchema,
    existing: z.boolean(),
});
/**
 * The bridge's refusals — `error.code` of the typed envelope. 404
 * `BID_NOT_FOUND` / `CAR_NOT_FOUND`; 409 `NOT_WON` (the bid is not `Won`);
 * 403 `NOT_ADMIN_BID` (the bidder holds no `admin` role — a client's win is
 * invoiced, never booked); 409 `UNKNOWN_LOCAL_AUCTION` (the lot's auction names
 * no `local_auctions` entry — `error.auctionName` says which name was tried).
 */
export const LOCAL_LEDGER_BRIDGE_ERROR_CODES = {
    BID_NOT_FOUND: 'BID_NOT_FOUND',
    CAR_NOT_FOUND: 'CAR_NOT_FOUND',
    NOT_WON: 'NOT_WON',
    NOT_ADMIN_BID: 'NOT_ADMIN_BID',
    UNKNOWN_LOCAL_AUCTION: 'UNKNOWN_LOCAL_AUCTION',
};
//# sourceMappingURL=local-ledger.js.map